import * as React from 'react';
import { isValidElement } from 'react';

/** Material UI Packages. */
import { Typography, IconButton, Tooltip } from '@mui/material';
import {
    useGridSelector,
    useGridApiContext,
    gridDetailPanelExpandedRowsContentCacheSelector,
    gridDetailPanelExpandedRowIdsSelector,
} from '@mui/x-data-grid-pro';

/** Material UI Icons. */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';

/**
 * Detail panel toggle cell for a `GRID_DETAIL_PANEL_TOGGLE_COL_DEF` column.
 *
 * The expanded state is read from `gridDetailPanelExpandedRowIdsSelector`, not from the cell's
 * `params.value`: that value is resolved when the cell renders and lags one click behind the
 * panel, which made the arrow point the wrong way (OS-365). Always pass only `id`.
 *
 * @param {object} props
 * @param {string|number} props.id Row id of the cell (`params.id`).
 * @param {React.ElementType} [props.icon] Icon component to rotate. Defaults to `ExpandMoreIcon`.
 * @param {string} [props.tooltip] Optional tooltip title wrapping the cell.
 * @param {'small'|'medium'|'large'|'lg'} [props.size] `IconButton` size. Defaults to `'lg'`.
 * @param {object} [props.sx] Overrides the wrapper `Typography` sx entirely.
 * @param {object} [props.iconSx] Merged into the icon's sx, after the rotation transform.
 * @param {React.ElementType} [props.component] Wrapper `Typography` element (e.g. `'div'`).
 */
export default function CustomDetailPanelToggle({
    id,
    icon: Icon = ExpandMoreIcon,
    tooltip,
    size = 'lg',
    sx,
    iconSx,
    component,
}) {
    const apiRef = useGridApiContext();
    const contentCache = useGridSelector(apiRef, gridDetailPanelExpandedRowsContentCacheSelector);
    const expandedRowIds = useGridSelector(apiRef, gridDetailPanelExpandedRowIdsSelector);
    const isExpanded = expandedRowIds.includes(id);
    const hasDetail = isValidElement(contentCache[id]);

    const cell = (
        <Typography component={component} sx={sx ?? { ...customized_style.itemDatagrid_Typography, width: '30px' }}>
            <IconButton size={size} tabIndex={-1} disabled={!hasDetail} aria-label={isExpanded ? 'Close' : 'Open'}>
                <Icon
                    sx={(theme) => ({
                        transform: `rotateZ(${isExpanded ? 180 : 0}deg)`,
                        transition: theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                        }),
                        ...iconSx,
                    })}
                    fontSize="inherit"
                />
            </IconButton>
        </Typography>
    );

    return tooltip ? <Tooltip title={tooltip}>{cell}</Tooltip> : cell;
}
